export interface BarcodeConfig {
  // Sticker dimensions
  height: number; // in mm
  width: number; // in mm
  
  // Layout
  orientation: OrientationEnum;
  
  stickersPerRow: number;
  rowsPerPage: number;
  
  pageMargin: number; // in mm
  stickerGap: number; // in mm
  rowGap: number; // in mm
  
  fontSize: {
    storeName: number;
    productName: number;
    price: number;
    productCode: number;
  };

  barcode: {
    width: number;
    height: number;
  };
  includeStoreName?: boolean;
  includeProductName?: boolean;
  includeSellingPrice?: boolean;
  includeMrp?: boolean;
  includeProductCode?: boolean;
  logoUrl?: string;
  logoMaxHeightMm?: number;
}

export enum OrientationEnum {
  Portrait = 'Portrait',
  Landscape = 'Landscape',
}
// API response interface
export interface ApiBarcodeConfig {
  stickerWidth: number;
  stickerHeight: number;
  stickersPerRow: number;
  orientation: OrientationEnum;
  stickerGap?: number;
  rowGap?: number;
  pageMargin?: number;
  fontSize?: {
    storeName: number;
    productName: number;
    price: number;
    productCode: number;
  };
  barcode?: {
    width: number;
    height: number;
  };
  logoUrl?: string;
  includeProductCode?: boolean;
  includeProductName?: boolean;
  includeStoreName?: boolean;
  includeSellingPrice?: boolean;
  includeMrp?: boolean;
  logoMaxHeightMm?: number;
}

// Default configuration (33mm x 25mm)
export const defaultBarcodeConfig: BarcodeConfig = {
  height: 30.5,
  width: 33,
  orientation: OrientationEnum.Landscape,
  stickersPerRow: 1,
  rowsPerPage: 1,
  pageMargin: 0,
  stickerGap: 0,
  rowGap: 0,
  fontSize: {
    storeName: 12,
    productName: 8,
    price: 11,
    productCode: 10,
  },
  barcode: {
    width: 35,
    height: 14,
  },
  includeStoreName: true,
  includeProductName: true,
  includeSellingPrice: true,
  includeMrp: true,
  includeProductCode: true,
  logoUrl: undefined,
  logoMaxHeightMm: 6,
};

class BarcodeConfigManager {
  private config: BarcodeConfig = defaultBarcodeConfig;
  
  updateConfig(newConfig: Partial<BarcodeConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
  
  // Update config from API data
  updateConfigFromApi(apiConfig: ApiBarcodeConfig): void {
    const mappedConfig: Partial<BarcodeConfig> = {
      width: apiConfig.stickerWidth,
      height: apiConfig.stickerHeight,
      stickersPerRow: apiConfig.stickersPerRow,
      orientation: apiConfig.orientation,
      stickerGap: apiConfig.stickerGap || this.config.stickerGap,
      rowGap: apiConfig.rowGap || this.config.rowGap,
      pageMargin: apiConfig.pageMargin || this.config.pageMargin,
      fontSize: apiConfig.fontSize || this.config.fontSize,
      barcode: apiConfig.barcode || this.config.barcode,
      logoUrl: apiConfig.logoUrl ?? this.config.logoUrl,
      includeProductCode:
        apiConfig.includeProductCode ?? this.config.includeProductCode,
      includeProductName:
        apiConfig.includeProductName ?? this.config.includeProductName,
      includeStoreName:
        apiConfig.includeStoreName ?? this.config.includeStoreName,
      includeSellingPrice:
        apiConfig.includeSellingPrice ?? this.config.includeSellingPrice,
      includeMrp: apiConfig.includeMrp ?? this.config.includeMrp,
      logoMaxHeightMm: apiConfig.logoMaxHeightMm ?? this.config.logoMaxHeightMm,
    };
    
    this.config = { ...this.config, ...mappedConfig };
  }
  
  getConfig(): BarcodeConfig {
    return { ...this.config };
  }
  calculateGridLayout(): {
    gridTemplateColumns: string;
    columnGap: string;
    rowGap: string;
  } {
    const { stickersPerRow, stickerGap, rowGap } = this.config;
    return {
      gridTemplateColumns: `repeat(${stickersPerRow}, 1fr)`,
      columnGap: `${stickerGap}mm`,
      rowGap: `${rowGap}mm`,
    };
  }
  
  getPagePadding(): string {
    return `${this.config.pageMargin}mm`;
  }
}

export const barcodeConfigManager = new BarcodeConfigManager();

// Export individual helper functions for direct use - UPDATED
export const calculateGridLayout = (
  stickersPerRow: number,
  stickerGap: number,
  rowGap: number = 0,
) => ({
  gridTemplateColumns: `repeat(${stickersPerRow}, 1fr)`,
  columnGap: `${stickerGap}mm`,
  rowGap: `${rowGap}mm`,
});

export const getPagePadding = (pageMargin: number) => `${pageMargin}mm`;